/**
 * A change-throughout card as the user checks it: the places the user keeps
 * checked, among the ones the card changes and the ones the agent left out, become
 * one `replace_span` each, applied with the engine the web applied the agent's
 * places with (`EditOperations.apply`) over the same reader paragraphs the card
 * carries (`ChangeThroughoutCardSummary.paragraphs`). The card's text redline then
 * reads those places changed and every other place as it is.
 *
 * WHY the engine, and never a splice of the new words at a place's offset
 *   The engine finds a place by its words and its occurrence with the one finder and
 *   checks the seams where the new words meet the paragraph; a second writer of the
 *   desired text would apply the user's places by a rule the agent's places never met.
 *
 * WHEN the engine hands a checked place back
 *   CONSEQUENCE
 *     it is set aside with the engine's reason and the rest are applied again, as the
 *     core does for the agent's places (`apply_kept_matches`): one place never stops
 *     the others.
 */
import { EditOperationReask, EditOperations, type DesiredParagraphText, type ParagraphEditOperation } from "./edit-operations";
import type { ChangeThroughoutCard, ChangeThroughoutMatch } from "./wire/change-throughout";
import type { RedlineTextParagraph, RedlineTextProposal } from "./wire/redline-payload";

/** A checked place the engine handed back: the place, the engine's reason enum and its sentence. */
export interface CheckedPlaceHandedBack {
    match: ChangeThroughoutMatch;
    reaskReason: string;
    reaskMessage: string;
}

/**
 * What the card applies for the places checked: its text redline with those places
 * changed, the places it changes, and the checked places the engine handed back.
 *
 * EMPTY
 *   `changedMatches` is [] when nothing is checked, or every checked place was handed
 *   back; the proposal then reads every paragraph as it is and changes nothing.
 */
export interface ChangeThroughoutSelectionProposal {
    proposal: RedlineTextProposal;
    changedMatches: ChangeThroughoutMatch[];
    checkedPlacesHandedBack: CheckedPlaceHandedBack[];
}

/**
 * Every place the user can check, in document order: the places the card changes and
 * the places the agent left out, paragraph by paragraph in the order the card lists its
 * paragraphs, and within a paragraph by occurrence. A place the web handed back is not
 * among them; the card shows it with its reason and no box.
 *
 * WHY the card's paragraphs set the order
 *   The core lists every paragraph that holds a place, in document order
 *   (`ChangeThroughoutCardSummary.paragraphs`), so walking them reads the places in the
 *   order they sit in the document.
 *
 * EXAMPLE
 *   changed [_result_prev_signed #1, _plus_border_sum #1], left out [_result_prev_signed #2]
 *   -> [_result_prev_signed #1, _result_prev_signed #2, _plus_border_sum #1]
 */
export function checkablePlacesOf(changeThroughoutCard: ChangeThroughoutCard): ChangeThroughoutMatch[] {
    const checkablePlaces = [...changeThroughoutCard.change_throughout.changed_matches, ...changeThroughoutCard.change_throughout.matches_left_out_by_the_agent];
    return changeThroughoutCard.change_throughout.paragraphs.flatMap((readerParagraph) =>
        checkablePlaces
            .filter((checkablePlace) => checkablePlace.paragraph_id === readerParagraph.paragraphBookmarkName)
            .sort((earlierPlace, laterPlace) => earlierPlace.occurrence - laterPlace.occurrence),
    );
}

/** The ids of the places the card changes as the agent proposed it: what the card shows checked before the user touches it. */
export function placesCheckedAsProposed(changeThroughoutCard: ChangeThroughoutCard): Set<string> {
    return new Set(changeThroughoutCard.change_throughout.changed_matches.map((changedPlace) => changedPlace.match_id));
}

/**
 * The card's text redline for the places checked (`ChangeThroughoutSelectionProposal`).
 *
 * EXAMPLE
 *   card over "Supplier pays. The Supplier delivers." (_result_prev_signed), "Supplier" -> "Vendor"
 *   checked [_result_prev_signed #2]
 *   -> _result_prev_signed textProposed "Supplier pays. The Vendor delivers."
 */
export function changeThroughoutProposalOf(changeThroughoutCard: ChangeThroughoutCard, checkedMatchIds: ReadonlySet<string>): ChangeThroughoutSelectionProposal {
    let remainingPlaces = checkablePlacesOf(changeThroughoutCard).filter((checkablePlace) => checkedMatchIds.has(checkablePlace.match_id));
    const checkedPlacesHandedBack: CheckedPlaceHandedBack[] = [];
    while (remainingPlaces.length > 0) {
        const holdingParagraphIds = new Set(remainingPlaces.map((remainingPlace) => remainingPlace.paragraph_id));
        try {
            const remainingPlacesApplied = EditOperations.apply({
                operations: remainingPlaces.map((remainingPlace) => replaceSpanOfPlace(changeThroughoutCard, remainingPlace)),
                paragraphs: changeThroughoutCard.change_throughout.paragraphs.filter(
                    (readerParagraph) => readerParagraph.paragraphBookmarkName !== null && holdingParagraphIds.has(readerParagraph.paragraphBookmarkName),
                ),
            });
            return { proposal: proposalReading(changeThroughoutCard, remainingPlacesApplied.paragraphs), changedMatches: remainingPlaces, checkedPlacesHandedBack };
        } catch (applyFailure) {
            /** WHEN the engine failed for any reason but a handed-back place CONSEQUENCE it propagates: that is a defect, not a place. */
            if (!(applyFailure instanceof EditOperationReask)) throw applyFailure;
            /**
             * `operationIndex` is 1-based: edit_ops[1] is the first remaining place.
             * EXAMPLE operationIndex 3 -> remainingPlaces[2]
             */
            const checkedPlaceHandedBack = remainingPlaces[applyFailure.operationIndex - 1];
            checkedPlacesHandedBack.push({ match: checkedPlaceHandedBack, reaskReason: applyFailure.reason, reaskMessage: applyFailure.message });
            remainingPlaces = remainingPlaces.filter((remainingPlace) => remainingPlace.match_id !== checkedPlaceHandedBack.match_id);
        }
    }
    return { proposal: proposalReading(changeThroughoutCard, []), changedMatches: [], checkedPlacesHandedBack };
}

/**
 * The one operation that changes one place, as the core writes it (`replace_span_of_match`):
 * the words as the agent typed them, at the place's occurrence, keeping any link the place's
 * words are the text of.
 *
 * WHY "keep"
 *   A change of the same words everywhere renames a term; where the term is a link's text,
 *   the link stays, at its address, around the new words. With no instruction the apply would
 *   ask for one, and with no model to answer, the place would be handed back.
 */
function replaceSpanOfPlace(changeThroughoutCard: ChangeThroughoutCard, placeToChange: ChangeThroughoutMatch): ParagraphEditOperation {
    return {
        op: "replace_span",
        paragraph_id: placeToChange.paragraph_id,
        anchor: changeThroughoutCard.change_throughout.text_now,
        text: changeThroughoutCard.change_throughout.text_wanted,
        occurrence: placeToChange.occurrence,
        link: "keep",
    };
}

/** The card's text redline with each paragraph as the places checked leave it (`cardParagraphUnderSelection`). */
function proposalReading(changeThroughoutCard: ChangeThroughoutCard, desiredParagraphTexts: DesiredParagraphText[]): RedlineTextProposal {
    const desiredParagraphTextById = new Map(desiredParagraphTexts.map((desiredParagraphText) => [desiredParagraphText.bookmark, desiredParagraphText]));
    return {
        ...changeThroughoutCard.redline,
        paragraphs: changeThroughoutCard.redline.paragraphs.map((cardParagraph) => cardParagraphUnderSelection(cardParagraph, desiredParagraphTextById)),
    };
}

/**
 * One card paragraph as the places checked leave it: the text it reads, and what becomes
 * of each link its checked places change.
 *
 * CASES
 *   - the engine wrote it              -> its desired text, and the link instructions its places recorded
 *   - no checked place sits in it      -> the text it read when proposed and no link instruction: it changes nothing
 *   - it carries no bookmark           -> the same: no operation can name it
 *
 * EXAMPLE
 *   _result_prev_signed "Supplier pays. The Supplier delivers.", desired "Supplier pays. The Vendor delivers."
 *   -> textProposed "Supplier pays. The Vendor delivers."
 */
function cardParagraphUnderSelection(cardParagraph: RedlineTextParagraph, desiredParagraphTextById: ReadonlyMap<string, DesiredParagraphText>): RedlineTextParagraph {
    const unchangedCardParagraph = { ...cardParagraph, textProposed: cardParagraph.textWhenProposed, linkInstructions: [] };
    /** NULL bookmark: a paragraph no operation can name reads as it was proposed. */
    if (cardParagraph.bookmark === null) return unchangedCardParagraph;
    const desiredParagraphText = desiredParagraphTextById.get(cardParagraph.bookmark);
    /** WHEN no checked place sits in the paragraph CONSEQUENCE it reads as it was proposed. */
    if (desiredParagraphText === undefined) return unchangedCardParagraph;
    return { ...cardParagraph, textProposed: desiredParagraphText.textProposed, linkInstructions: desiredParagraphText.linkInstructions };
}
